// Where a job's crew run lands when the scheduler shifts it by N days.
//
// Lives in lib/ with no runtime imports beyond the date helpers, on purpose:
// this is the destructive half of the ⇄ Shift button, and keeping it free of app
// wiring means it can be tested directly. (Importing it from ManpowerPage would
// drag in apiClient → MSAL config, which needs a browser env.) Same reasoning,
// and the same shape, as planSetupCells.
//
// Extracted in answer to #452, which reported a 7-day shift landing 6 days
// later. It had not: the run moved Mon Sep 14 → Mon Sep 21, and the Sunday the
// reporter read was a week-of label on the project's Scheduled Crew card. But
// the arithmetic was untested and buried in a 1,800-line component, so
// answering a report about it took a production database read rather than a
// test run — the second such report after #286. Hence this file.
import type { CrewPhase } from './scheduleModel';
import { addDays, localYmd, entryDateStr } from './gridDates';

/** One crew day as the per-project feed returns it. */
export interface ShiftCrewDay {
  workDate: string;
  /** The feed types this as a bare string; narrowed on the way in. */
  phase: string | null;
  crewCount: number;
  isOverride?: boolean;
}

/** A draft row the shift stages. Assignable to DraftEntryInput. */
export interface ShiftDraftEntry {
  projectId: number;
  workDate: string;
  phase: CrewPhase;
  crewCount: number;
  isOverride: boolean;
}

export interface ShiftPlan {
  /**
   * Draft rows to stage, in order: every landing cell first, then a zero on
   * each day the run left. Order is load-bearing — see `entries` construction.
   */
  entries: ShiftDraftEntry[];
  /** Crew days the move covers. What the scheduler is told moved. */
  movedDays: number;
  /**
   * Days the run lands on that already carry crew it is not itself vacating —
   * i.e. cells this move would overwrite. Drives the confirm prompt.
   */
  collisions: number;
}

/**
 * `ymd` moved by `offset` calendar days.
 *
 * Anchored at NOON, and that is not cosmetic. A bare `YYYY-MM-DD` parses as UTC
 * midnight, which in Eastern is the previous evening — so a midnight anchor
 * reads back a day early. Noon also clears both DST transitions, where a local
 * day is 23 or 25 hours long.
 */
export function shiftYmd(ymd: string, offset: number): string {
  return localYmd(addDays(new Date(`${ymd}T12:00:00`), offset));
}

/**
 * Plan a job shift: which cells the run vacates, which it lands on, and what
 * it would overwrite getting there.
 *
 * Three rules carry the risk:
 *
 *  1. THE PAST DOES NOT MOVE. Days before `today` are booked history — the same
 *     boundary "Clear future crew" draws. They still have to be PASSED IN,
 *     because rule 3 needs to see what the run would land on; they are filtered
 *     out of the move here rather than at the call site so the two can never
 *     disagree about where the past starts. A negative offset can still pull
 *     crew back before today: that is what pulling a job earlier means.
 *
 *  2. A vacated day the move refills is never then zeroed. The run overlaps
 *     itself on any offset shorter than its own length, so most cells are both
 *     a source and a destination; zeroing those would punch holes through the
 *     middle of the moved run.
 *
 *  3. Landing on a day that already carries crew OVERWRITES it, so the caller
 *     has to be able to confirm first. Cells the run is vacating anyway don't
 *     count — they aren't a loss.
 */
export function planShiftedRun(opts: {
  projectId: number;
  /** The job's crew run from the per-project feed. May span before `today`. */
  crewDays: ShiftCrewDay[];
  /** YYYY-MM-DD. Days strictly before this are history and never move. */
  today: string;
  offset: number;
}): ShiftPlan {
  const { projectId, crewDays, today, offset } = opts;

  // Rule 1.
  const moving = crewDays.filter((e) => entryDateStr(e.workDate) >= today);

  const from = (e: ShiftCrewDay) => entryDateStr(e.workDate);
  const to = (e: ShiftCrewDay) => shiftYmd(from(e), offset);

  // Narrow the feed's bare string to the union the draft layer stores, so an
  // unrecognised value cannot ride through as a phase. Every key below narrows
  // through the same function: a cell keyed one way and written another would
  // vacate a row that was never refilled.
  const phaseOf = (p: string | null): CrewPhase =>
    p === 'fabrication' || p === 'installation' ? p : null;
  const cellKey = (workDate: string, phase: CrewPhase) => `${workDate}|${phase ?? ''}`;

  // Rule 3.
  const vacating = new Set(moving.map((e) => cellKey(from(e), phaseOf(e.phase))));
  const collisions = moving.filter((e) => {
    const landing = cellKey(to(e), phaseOf(e.phase));
    if (vacating.has(landing)) return false;
    return crewDays.some((o) => cellKey(from(o), phaseOf(o.phase)) === landing);
  });

  const refills: ShiftDraftEntry[] = moving.map((e) => ({
    projectId,
    workDate: to(e),
    phase: phaseOf(e.phase),
    crewCount: e.crewCount,
    isOverride: e.isOverride ?? false,
  }));

  // Rule 2. Refills are staged first and win; a zero is dropped if a refill has
  // already claimed its cell.
  const refilled = new Set(refills.map((e) => cellKey(e.workDate, e.phase)));
  const zeroes: ShiftDraftEntry[] = moving
    .map((e) => ({
      projectId,
      workDate: from(e),
      phase: phaseOf(e.phase),
      crewCount: 0,
      isOverride: false,
    }))
    .filter((e) => !refilled.has(cellKey(e.workDate, e.phase)));

  return {
    entries: [...refills, ...zeroes],
    movedDays: moving.length,
    collisions: collisions.length,
  };
}
